import { create } from 'zustand';
import type { CustomFont } from '@proclaim/shared';
import { api } from '../shared/api';
import { useToastStore } from './toast-store';

interface FontState {
  customFonts: CustomFont[];
  loading: boolean;
  fetchFonts: () => Promise<void>;
  createFamily: (label: string) => Promise<CustomFont>;
  addFile: (fontId: string, file: File, weight: number, style: 'normal' | 'italic', variable: boolean) => Promise<CustomFont>;
  deleteFile: (fontId: string, fileId: string) => Promise<void>;
  deleteFamily: (id: string) => Promise<void>;
}

export const useFontStore = create<FontState>((set, _get) => ({
  customFonts: [],
  loading: false,

  fetchFonts: async () => {
    set({ loading: true });
    try {
      const fonts = await api.getFonts();
      set({ customFonts: fonts, loading: false });
    } catch {
      set({ loading: false });
      useToastStore.getState().show('error', 'No se pudieron cargar las fuentes personalizadas');
    }
  },

  createFamily: async (label) => {
    const font = await api.createFontFamily(label);
    set((s) => ({ customFonts: [...s.customFonts, font] }));
    return font;
  },

  addFile: async (fontId, file, weight, style, variable) => {
    const updatedFamily = await api.addFontFile(fontId, file, weight, style, variable);
    set((s) => ({
      customFonts: s.customFonts.map((f) => (f.id === fontId ? updatedFamily : f)),
    }));
    return updatedFamily;
  },

  deleteFile: async (fontId, fileId) => {
    await api.deleteFontFile(fileId);
    set((s) => ({
      customFonts: s.customFonts.map((f) =>
        f.id === fontId ? { ...f, files: f.files.filter((ff) => ff.id !== fileId) } : f,
      ),
    }));
  },

  deleteFamily: async (id) => {
    await api.deleteFont(id);
    set((s) => ({ customFonts: s.customFonts.filter((f) => f.id !== id) }));
  },
}));
